import React, { useState, useEffect } from 'react';
import { authService } from '../services/authService.ts';
import { User, Permission, ROLE_PERMISSIONS, ALL_PERMISSIONS, PERMISSION_DESCRIPTIONS, UserRole } from '../types/auth.ts';
import PermissionGate from '../components/PermissionGate.tsx';
import { useAppContext } from '../contexts/AppContext.tsx';
import { useAuth } from '../contexts/AuthContext.tsx';
import { formatPhoneNumber, extractPhoneNumbers } from '../utils/validationUtils.ts';

const UserManagementPage: React.FC = () => {
  const { teams, employees } = useAppContext();
  const { user } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // 전화번호 편집을 위한 로컬 상태 추가
  const [editingPhoneNumber, setEditingPhoneNumber] = useState<string>('');
  
  // 새로운 사용자 추가 관련 상태
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [newUserData, setNewUserData] = useState({
    email: '',
    password: '',
    displayName: '',
    phoneNumber: '',
    role: 'viewer' as UserRole
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  // selectedUser가 변경될 때마다 editingPhoneNumber 상태 초기화
  useEffect(() => {
    if (selectedUser) {
      setEditingPhoneNumber(formatPhoneNumber(selectedUser.phoneNumber || ''));
    }
  }, [selectedUser]);

  const loadUsers = async (): Promise<User[]> => {
    try {
      setLoading(true);
      setError(null);

      // Firebase Auth 사용자를 Firestore에 동기화 후 모든 사용자 로드
      const allUsers = await authService.getAllUsers();
      setUsers(allUsers);

      console.log(`UserManagement: Loaded ${allUsers.length} users (server)`,
        allUsers.map(u => ({ uid: u.uid.slice(0, 8), role: u.role, email: u.email })));
      return allUsers;
    } catch (error: any) {
      console.error('사용자 목록 로드 실패:', error);
      setError('사용자 목록을 불러오는데 실패했습니다.');
      return [];
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionToggle = async (
    user: User, 
    permission: Permission, 
    type: 'add' | 'revoke'
  ) => {
    try {
      setUpdating(true);
      
      if (type === 'add') {
        await authService.addPermissionToUser(user.uid, permission);
      } else {
        await authService.removePermissionFromUser(user.uid, permission);
      }

      const freshUsers = await loadUsers();

      // 선택된 사용자 정보도 업데이트 (fresh 데이터 사용)
      if (selectedUser && selectedUser.uid === user.uid) {
        const updatedUser = freshUsers.find(u => u.uid === user.uid);
        if (updatedUser) {
          setSelectedUser(updatedUser);
        }
      }
    } catch (error: any) {
      console.error('권한 업데이트 실패:', error);
      setError('권한 업데이트에 실패했습니다.');
    } finally {
      setUpdating(false);
    }
  };

  const handleRoleChange = async (user: User, newRole: UserRole) => {
    try {
      setUpdating(true);
      await authService.updateUserRole(user.uid, newRole);
      const freshUsers = await loadUsers();

      // 선택된 사용자 정보도 업데이트 (fresh 데이터 사용)
      if (selectedUser && selectedUser.uid === user.uid) {
        const updatedUser = freshUsers.find(u => u.uid === user.uid);
        if (updatedUser) {
          setSelectedUser(updatedUser);
        }
      }
    } catch (error: any) {
      console.error('역할 업데이트 실패:', error);
      setError('역할 업데이트에 실패했습니다.');
    } finally {
      setUpdating(false);
    }
  };

  /**
   * 계정 ↔ 직원 명단 수동 연결.
   *
   * 🔒 이름으로 자동 매칭하지 않는다. 동명이인이 있으면 엉뚱한 사람의 급여가
   *    "본인 급여"로 열리기 때문에, 연결은 반드시 원장이 눈으로 보고 지정한다.
   */
  const handleEmployeeLinkChange = async (targetUser: User, employeeId: string) => {
    try {
      setUpdating(true);
      setError(null);

      // 🔒 사번이 겹치는 직원이 있으면 연결을 거부한다.
      //    같은 사번을 가진 두 사람 중 누구인지 시스템이 구분할 수 없어,
      //    엉뚱한 사람의 급여가 '본인 급여'로 열릴 수 있다.
      if (employeeId) {
        const 동일사번 = employees.filter(emp => String(emp.id) === String(employeeId));
        if (동일사번.length > 1) {
          const 명단 = 동일사번.map(e => `${e.name}(${e.team || '팀 미지정'}·${e.position || '직위 미지정'})`).join(', ');
          setError(
            `사번 ${employeeId}번을 쓰는 직원이 ${동일사번.length}명입니다: ${명단}. ` +
            '누구인지 구분할 수 없어 연결하지 않았습니다. 직원 명단에서 사번을 서로 다르게 정리한 뒤 다시 지정해 주세요.'
          );
          return;
        }
      }

      await authService.updateUserEmployeeId(targetUser.uid, employeeId);
      const freshUsers = await loadUsers();
      if (selectedUser && selectedUser.uid === targetUser.uid) {
        const updatedUser = freshUsers.find(u => u.uid === targetUser.uid);
        if (updatedUser) setSelectedUser(updatedUser);
      }
    } catch (error: any) {
      console.error('직원 연결 실패:', error);
      setError(error?.message || '직원 연결에 실패했습니다.');
    } finally {
      setUpdating(false);
    }
  };

  const handleTeamChange = async (user: User, teamId: string) => {
    try {
      setUpdating(true);
      await authService.updateUserTeam(user.uid, teamId);
      const freshUsers = await loadUsers();

      // 선택된 사용자 정보도 업데이트 (fresh 데이터 사용)
      if (selectedUser && selectedUser.uid === user.uid) {
        const updatedUser = freshUsers.find(u => u.uid === user.uid);
        if (updatedUser) {
          setSelectedUser(updatedUser);
        }
      }
    } catch (error: any) {
      console.error('팀 배정 실패:', error);
      setError('팀 배정에 실패했습니다.');
    } finally {
      setUpdating(false);
    }
  };

  const handlePhoneNumberChange = async (user: User, phoneNumber: string) => {
    try {
      setUpdating(true);
      // 전화번호에서 숫자만 추출해서 저장
      const numbersOnly = extractPhoneNumbers(phoneNumber);
      await authService.updateUserPhoneNumber(user.uid, numbersOnly);
      
      // 최신 사용자 목록을 직접 가져와서 상태 업데이트
      const updatedUsers = await authService.getAllUsers();
      setUsers(updatedUsers);
      
      // 선택된 사용자 정보도 업데이트
      if (selectedUser && selectedUser.uid === user.uid) {
        const updatedUser = updatedUsers.find(u => u.uid === user.uid);
        if (updatedUser) {
          setSelectedUser(updatedUser);
          // editingPhoneNumber 상태도 업데이트
          const newFormattedPhone = formatPhoneNumber(updatedUser.phoneNumber || '');
          setEditingPhoneNumber(newFormattedPhone);
          console.log('🔄 전화번호 UI 업데이트:', {
            이전: formatPhoneNumber(selectedUser.phoneNumber || ''),
            현재: newFormattedPhone,
            원본: updatedUser.phoneNumber
          });
        }
      }
    } catch (error: any) {
      console.error('전화번호 업데이트 실패:', error);
      setError('전화번호 업데이트에 실패했습니다.');
      // 실패 시 원래 값으로 복원
      if (selectedUser) {
        setEditingPhoneNumber(formatPhoneNumber(selectedUser.phoneNumber || ''));
      }
    } finally {
      setUpdating(false);
    }
  };

  // 새 사용자 전화번호 입력 핸들러 (자동 포맷팅 적용)
  const handleNewUserPhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const formatted = formatPhoneNumber(value);
    setNewUserData({...newUserData, phoneNumber: formatted});
  };

  // 새로운 사용자 생성 함수
  const handleCreateUser = async () => {
    if (!newUserData.email || !newUserData.password) {
      setError('이메일과 비밀번호를 입력해주세요.');
      return;
    }

    if (newUserData.password.length < 6) {
      setError('비밀번호는 최소 6자 이상이어야 합니다.');
      return;
    }

    setCreating(true);
    try {
      await authService.createUserByAdmin({
        email: newUserData.email,
        password: newUserData.password,
        displayName: newUserData.displayName || undefined,
        phoneNumber: newUserData.phoneNumber ? extractPhoneNumbers(newUserData.phoneNumber) : undefined,
        role: newUserData.role
      });

      setNewUserData({
        email: '',
        password: '',
        displayName: '',
        phoneNumber: '',
        role: 'viewer'
      });
      setShowAddUserModal(false);
      await loadUsers();
      setError(null);
    } catch (error: any) {
      console.error('사용자 생성 실패:', error);
      setError(error.message);
    } finally {
      setCreating(false);
    }
  };

  // 사용자 삭제 함수
  const handleDeleteUser = async (userToDelete: User) => {
    // 관리자 권한 체크
    if (user?.role !== 'admin') {
      setError('사용자 삭제는 관리자만 가능합니다.');
      return;
    }
    
    // 자기 자신 삭제 방지
    if (user?.uid === userToDelete.uid) {
      setError('자기 자신은 삭제할 수 없습니다.');
      return;
    }
    
    if (!window.confirm(`정말로 "${userToDelete.displayName || userToDelete.email}" 사용자를 삭제하시겠습니까?\n\n이 작업은 되돌릴 수 없습니다.`)) {
      return;
    }
    
    try {
      setUpdating(true);
      await authService.deleteUserFromFirestore(userToDelete.uid);
      await loadUsers(); // 목록 새로고침
      
      // 삭제된 사용자가 선택되어 있었다면 선택 해제
      if (selectedUser?.uid === userToDelete.uid) {
        setSelectedUser(null);
      }
      
      setError(null);
    } catch (error: any) {
      console.error('사용자 삭제 실패:', error);
      setError(error.message);
    } finally {
      setUpdating(false);
    }
  };

  const getPermissionStatus = (user: User, permission: Permission) => {
    const rolePermissions = ROLE_PERMISSIONS[user.role];
    const hasRolePermission = rolePermissions.includes(permission);
    const hasCustomPermission = user.customPermissions?.includes(permission);
    const isRevoked = user.revokedPermissions?.includes(permission);
    
    const effectivePermission = (hasRolePermission || hasCustomPermission) && !isRevoked;
    
    return {
      hasPermission: effectivePermission,
      isFromRole: hasRolePermission,
      isCustom: hasCustomPermission,
      isRevoked
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <PermissionGate permissions={['manage_users']}>
      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">사용자 권한 관리</h1>
              <p className="text-gray-600">사용자별로 개별 권한을 설정하고 관리할 수 있습니다.</p>
            </div>
            <button
              onClick={() => setShowAddUserModal(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              + 새 사용자 추가
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded">
            {error}
            <button 
              onClick={() => setError(null)}
              className="ml-2 text-red-500 hover:text-red-700"
            >
              ✕
            </button>
          </div>
        )}
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* 사용자 목록 */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-lg font-medium text-gray-900">사용자 목록</h2>
                  <p className="text-sm text-gray-500">총 {users.length}명의 사용자</p>
                </div>
                <button
                  onClick={loadUsers}
                  disabled={loading}
                  className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? '동기화 중...' : '새로고침'}
                </button>
              </div>
            </div>
            
            <div className="p-4 space-y-2 max-h-96 overflow-y-auto">
              {users.map(userItem => (
                <div 
                  key={userItem.uid}
                  onClick={() => setSelectedUser(userItem)}
                  className={`p-3 rounded cursor-pointer border transition-colors ${
                    selectedUser?.uid === userItem.uid 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-900">
                        {userItem.displayName || userItem.email.split('@')[0]}
                      </div>
                      <div className="text-sm text-gray-500">{userItem.email}</div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-2">
                        <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          userItem.role === 'admin' ? 'bg-red-100 text-red-800' :
                          userItem.role === 'manager' ? 'bg-yellow-100 text-yellow-800' :
                          userItem.role === 'viewer' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {userItem.role}
                        </div>
                        {user?.role === 'admin' && userItem.uid !== user.uid && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteUser(userItem);
                            }}
                            disabled={updating}
                            className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50"
                            title="사용자 삭제"
                          >
                            삭제
                          </button>
                        )}
                      </div>
                      {userItem.role === 'employee' && userItem.teamId && (
                        <div className="text-xs text-blue-600 mt-1">
                          {teams.find(t => t.id === userItem.teamId)?.name || '팀 미지정'}
                        </div>
                      )}
                      <div className="text-xs text-gray-500 mt-1">
                        {userItem.permissions.length}개 권한
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 권한 편집 */}
          {selectedUser && (
            <div className="bg-white rounded-lg shadow">
              <div className="p-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">
                  {selectedUser.displayName || selectedUser.email} 권한 설정
                </h2>
                <p className="text-sm text-gray-500">{selectedUser.email}</p>
              </div>
              
              <div className="p-4 space-y-6">
                {/* 역할 변경 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    사용자 역할
                  </label>
                  <select
                    value={selectedUser.role}
                    onChange={(e) => handleRoleChange(selectedUser, e.target.value as UserRole)}
                    disabled={updating}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="admin">관리자 (Admin)</option>
                    <option value="manager">매니저 (Manager)</option>
                    <option value="viewer">뷰어 (Viewer)</option>
                    <option value="employee">직원 (Employee)</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    기본 권한: {ROLE_PERMISSIONS[selectedUser.role].length}개
                  </p>
                </div>

                {/* 직원 역할인 경우 팀 선택 */}
                {selectedUser.role === 'employee' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      소속 팀
                    </label>
                    <select
                      value={selectedUser.teamId || ''}
                      onChange={(e) => handleTeamChange(selectedUser, e.target.value)}
                      disabled={updating}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">팀 선택</option>
                      {teams.map(team => (
                        <option key={team.id} value={team.id}>
                          {team.name}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      직원 역할 사용자는 반드시 팀에 소속되어야 합니다.
                    </p>
                  </div>
                )}

                {/* 본인 급여 열람을 위한 직원 연결 — 역할과 무관하게 지정할 수 있다.
                    실장은 manager 역할이므로 'employee'로 한정하면 정작 필요한 계정에서 빠진다. */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    연결된 직원 (본인 급여 열람)
                  </label>
                  <select
                    value={selectedUser.employeeId || ''}
                    onChange={(e) => handleEmployeeLinkChange(selectedUser, e.target.value)}
                    disabled={updating}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">연결 안 함</option>
                    {employees.map(emp => (
                      <option key={emp.id} value={String(emp.id)}>
                        {emp.name} ({emp.team || '팀 미지정'} · {emp.position || '직위 미지정'})
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    연결한 직원의 급여만 이 계정에서 본인 것으로 열람됩니다. 동명이인이 있을 수 있으니 팀·직위를 확인하고 지정하세요.
                  </p>
                </div>

                {/* 전화번호 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    전화번호
                  </label>
                  <div className="flex gap-2">
                    <input
                      key={selectedUser.uid}
                      type="tel"
                      value={editingPhoneNumber}
                      onChange={(e) => {
                        // 입력 중 자동 포맷팅 적용
                        const formatted = formatPhoneNumber(e.target.value);
                        setEditingPhoneNumber(formatted);
                      }}
                      onBlur={(e) => {
                        const phoneNumber = e.target.value.trim();
                        const currentFormatted = formatPhoneNumber(selectedUser.phoneNumber || '');
                        if (phoneNumber !== currentFormatted) {
                          handlePhoneNumberChange(selectedUser, phoneNumber);
                        }
                      }}
                      disabled={updating}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="010-1234-5678"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    전화번호를 입력하고 포커스를 벗어나면 자동으로 저장됩니다.
                  </p>
                </div>

                {/* 권한 목록 */}
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-3">개별 권한 설정</h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {ALL_PERMISSIONS.map(permission => {
                      const status = getPermissionStatus(selectedUser, permission);
                      
                      return (
                        <div key={permission} className="flex items-center justify-between p-2 border rounded-md bg-gray-50">
                          <div className="flex-1">
                            <div>
                              <span className="text-sm font-medium text-gray-900">
                                {permission.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              </span>
                              <p className="text-xs text-gray-500 mt-0.5">
                                {PERMISSION_DESCRIPTIONS[permission]}
                              </p>
                            </div>
                            <div className="flex gap-1 mt-1">
                              {status.isFromRole && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                  역할
                                </span>
                              )}
                              {status.isCustom && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                  추가
                                </span>
                              )}
                              {status.isRevoked && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                  제거됨
                                </span>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex gap-2">
                            {!status.hasPermission && (
                              <button
                                onClick={() => handlePermissionToggle(selectedUser, permission, 'add')}
                                disabled={updating}
                                className="px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50"
                              >
                                추가
                              </button>
                            )}
                            
                            {status.hasPermission && (
                              <button
                                onClick={() => handlePermissionToggle(selectedUser, permission, 'revoke')}
                                disabled={updating}
                                className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50"
                              >
                                제거
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* 권한 요약 */}
                <div className="bg-gray-50 p-3 rounded-md">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">권한 요약</h4>
                  <div className="text-sm text-gray-600 space-y-1">
                    <div>총 권한: {selectedUser.permissions.length}개</div>
                    <div>역할 권한: {ROLE_PERMISSIONS[selectedUser.role].length}개</div>
                    <div>추가 권한: {selectedUser.customPermissions?.length || 0}개</div>
                    <div>제거된 권한: {selectedUser.revokedPermissions?.length || 0}개</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {!selectedUser && (
          <div className="lg:col-start-2 bg-gray-50 rounded-lg p-8 text-center">
            <div className="text-gray-500">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-2.25" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">사용자를 선택하세요</h3>
              <p className="mt-1 text-sm text-gray-500">
                왼쪽 목록에서 사용자를 선택하여 권한을 관리하세요.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* 새로운 사용자 추가 모달 */}
      {showAddUserModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                      새 사용자 추가
                    </h3>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          이메일 *
                        </label>
                        <input
                          type="email"
                          value={newUserData.email}
                          onChange={(e) => setNewUserData({...newUserData, email: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="user@example.com"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          임시 비밀번호 *
                        </label>
                        <input
                          type="password"
                          value={newUserData.password}
                          onChange={(e) => setNewUserData({...newUserData, password: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="최소 6자 이상"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          표시 이름
                        </label>
                        <input
                          type="text"
                          value={newUserData.displayName}
                          onChange={(e) => setNewUserData({...newUserData, displayName: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="선택사항"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          전화번호
                        </label>
                        <input
                          type="tel"
                          value={newUserData.phoneNumber}
                          onChange={handleNewUserPhoneNumberChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="010-1234-5678 (선택사항)"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          사용자 역할
                        </label>
                        <select
                          value={newUserData.role}
                          onChange={(e) => setNewUserData({...newUserData, role: e.target.value as UserRole})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="viewer">뷰어 (Viewer)</option>
                          <option value="employee">직원 (Employee)</option>
                          <option value="manager">매니저 (Manager)</option>
                          <option value="admin">관리자 (Admin)</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleCreateUser}
                  disabled={creating || !newUserData.email || !newUserData.password}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  {creating ? '생성 중...' : '사용자 생성'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddUserModal(false);
                    setNewUserData({
                      email: '',
                      password: '',
                      displayName: '',
                      phoneNumber: '',
                      role: 'viewer'
                    });
                  }}
                  disabled={creating}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  취소
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </PermissionGate>
  );
};

export default UserManagementPage;